import { Component, Input, OnInit } from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { MatDialog } from '@angular/material/dialog';
import { Subject, SubjectMarker } from '../../../../shared/models/subject';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from 'app/components/messageBox/messageBox.component';
import { SelectMarkerComponent } from './select-marker/select-marker.component';
import { AuthService } from 'app/shared/services/auth.service';
import { EventService } from 'app/shared/services/event.service';
import { MarkerService } from 'app/shared/services/marker.service';
import { SubjectService } from 'app/shared/services/subject.service';

@Component({
  selector: 'marker-list-widget',
  templateUrl: './marker-list-widget.component.html',
  styleUrls: ['./marker-list-widget.component.scss'],
  animations: fuseAnimations,
})
export class MarkerListWidgetComponent implements OnInit {
  _subject: Subject;
  title = '';
  data = [];
  selected: any;
  allowEdit = false;
  userReadOnly = false;
  sortedAscending = false;
  markers = [];

  //added empty inputs to remove errors - YA
  @Input() id: Number;
  @Input() subjectId: Number;
  @Input() processTypeId: Number;
  @Input() settings: any;

  @Input() set subject(val: Subject) {
    if (val) {
      this.update(val);
    }
  }

  constructor(
    private subjectService: SubjectService,
    private eventService: EventService,
    private authService: AuthService,
    private markerService: MarkerService,
    public dialog: MatDialog
  ) {
    this.userReadOnly = this.authService.isUserReadOnly();
  }

  ngOnInit() {
    this.title = 'Markers';
    this.markerService.getAllData().subscribe((response) => {
      this.markers = response;
    });
  }

  select(row) {
    this.selected = row;
  }

  edit() {
    this.dialog
      .open(SelectMarkerComponent, { data: { subject: this._subject } })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          // adjust the subject markers
          const currentSubjectMarkers = this._subject.subjectMarkers.map(
            (i) => i.marker.id
          );
          this._subject.subjectMarkers = this._subject.subjectMarkers.filter(
            (i) => result.selectedIds.indexOf(i.markerId) !== -1
          );
          const added = result.selectedIds.filter(
            (i) => currentSubjectMarkers.indexOf(i) === -1
          );
          added.forEach((i) => {
            this._subject.subjectMarkers.push(
              new SubjectMarker({
                markerId: i,
                marker: this.markers.find((x) => x.id === i),
              })
            );
          });

          if (
            added.length > 0 ||
            currentSubjectMarkers.length > this._subject.subjectMarkers.length
          ) {
            // update display
            this.update();
            // make sure listeners are notified of a change to the subject
            this.eventService.markSubjectAsDirty(
              this._subject.id,
              'Markers Updated'
            );
          }
        }
      });
  }

  delete(row) {
    row = row || this.selected;
    const subjectMarker = row.subjectMarker;
    const info = new MessageBoxParameters(
      'Are you sure you want to remove this marker?',
      'Confirm',
      MessageBoxType.Confirm,
      true
    );
    this.dialog
      .open(MessageBoxComponent, {
        data: info,
        disableClose: false,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'confirm') {
          this._subject.subjectMarkers = this._subject.subjectMarkers.filter(
            (i) => i.markerId !== row.markerId
          );
          // update display
          this.update();
          // notify listeners of change to subject
          this.eventService.markSubjectAsDirty(
            this._subject.id,
            'Marker Removed'
          );
        }
      });
  }

  update(subject?: Subject) {
    subject = subject || this._subject;
    if (subject) {
      this._subject = subject;
    }
    if (this._subject) {
      this.allowEdit =
        this._subject.uid && !this._subject.archived && !this.userReadOnly;
      const markers = [];
      this._subject.subjectMarkers.forEach((subjectMarker) => {
        markers.push({
          id: subjectMarker.id,
          markerId: subjectMarker.marker.id,
          title: subjectMarker.marker.name,
          secondary: subjectMarker.marker.description,
          subjectMarker,
        });
      });
      this.applySortOrder(markers, this.subjectService.markerSortAscending);
    }
  }

  applySortOrder(markers: any[], ascending?: boolean) {
    if (markers && markers.length > 0) {
      this.sortedAscending = ascending ? true : false;
      this.subjectService.markerSortAscending = this.sortedAscending;
      this.data = ascending
        ? markers.sort((a, b) => a.title.localeCompare(b.title))
        : markers.sort((a, b) => b.title.localeCompare(a.title));
    } else {
      this.data = [];
    }
  }

  toggleSortOrder() {
    this.applySortOrder(this.data, !this.sortedAscending);
  }
}
